import React from 'react';
import { Table, Tag, Row, Col, Button, Popconfirm, Grid, Tooltip } from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const ImageRequestsTable = ({
  items = [],
  loading = false,
  currentPage = 1,
  perPage = 10,
  total = 0,
  onPageChange,
  loggedInUser,
  t,
  onStart,
  onOpenComplete,
  onDelete,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const columns = [
    {
      title: t('vehicles.vin'),
      dataIndex: 'vin',
      key: 'vin',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 180,
      render: (v) => <span style={{ overflowWrap: 'normal' }}>{v || '-'}</span>,
    },
    {
      title: t('vehicles.make'),
      dataIndex: 'make',
      key: 'make',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 100,
    },
    {
      title: t('vehicles.model'),
      dataIndex: 'model',
      key: 'model',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 350,
      render: (v) => <span style={{ overflowWrap: 'normal' }}>{v || '-'}</span>,
    },
    {
      title: t('form.vehicleForm.vehicleAdditionalDetails.color'),
      dataIndex: 'color',
      key: 'color',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 140,
      render: (v) => <span style={{ overflowWrap: 'normal' }}>{v || '-'}</span>,
    },
    {
      title: t('form.vehicleForm.vehicleAdditionalDetails.modelYear'),
      dataIndex: 'year',
      key: 'year',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 120,
      render: (v) => <span style={{ overflowWrap: 'normal' }}>{v || '-'}</span>,
    },
    {
      title: t('imageRequests.columns.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => (v ? moment(v).local().format('L LT') : '-'),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 170,
    },
    {
      title: t('imageRequests.columns.completed'),
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (v) => (v ? moment(v).local().format('L LT') : '-'),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 170,
    },
    {
      title: t('imageRequests.columns.priority'),
      dataIndex: 'priority',
      key: 'priority',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 70,
    },
    {
      title: t('imageRequests.columns.system'),
      dataIndex: 'system',
      key: 'system',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      width: 120,
    },
    {
      title: t('imageRequests.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (v) => {
        const s = (v || '').toString().toLowerCase();
        const color =
          s === 'completed' ? 'green' : s === 'inprogress' ? 'blue' : 'orange';
        const label =
          s === 'completed'
            ? t('imageRequests.statusLabel.completed')
            : s === 'inprogress'
            ? t('imageRequests.statusLabel.inProgress')
            : s === 'pending'
            ? t('imageRequests.statusLabel.pending')
            : (v || '-').toString().toUpperCase();
        return <Tag color={color}>{label}</Tag>;
      },
      width: 140,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: t('imageRequests.columns.assignedTo'),
      key: 'assignedTo',
      width: 200,
      render: (_, record) => {
        const u = record.assignedToUser || record.assignedTo;
        if (!u) return '-';
        if (typeof u === 'string') return u;
        if (typeof u === 'object') {
          const fn = u.firstName || '';
          const ln = u.lastName || '';
          const name = `${fn} ${ln}`.trim();
          const email = u.email || '';
          if (name && email) return `${name} (${email})`;
          if (name) return name;
          return email || u.id || '-';
        }
        return '-';
      },
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => {
        const status = record.status || '';
        const assignedId =
          record.assignedToUser?.id ||
          (typeof record.assignedTo === 'string'
            ? record.assignedTo
            : record.assignedTo?.id || record.assignedTo?._id || null);
        const me = loggedInUser?.id || loggedInUser?._id;
        const isAssignee = assignedId && me && assignedId === me;
        const role = loggedInUser?.role?.name;
        const isRoot = role === 'root';
        const canDelete = isRoot || role === 'admin';

        const actionBtns = [];
        const isMobile =
          typeof window !== 'undefined' && window.innerWidth < 576;
        if (!isRoot && status === 'pending') {
          const startBtn = (
            <Button
              key="start"
              type="primary"
              ghost={!isMobile}
              size="small"
              shape={isMobile ? 'circle' : undefined}
              icon={<PlayCircleOutlined />}
              onClick={() => onStart(record)}
              style={
                isMobile ? {} : { borderColor: '#1677ff', color: '#1677ff' }
              }
            >
              {!isMobile && t('imageRequests.actions.start')}
            </Button>
          );
          actionBtns.push(
            isMobile ? (
              <Tooltip title={t('imageRequests.actions.start')}>
                {startBtn}
              </Tooltip>
            ) : (
              startBtn
            )
          );
        }
        if (!isRoot && status === 'inProgress' && isAssignee) {
          const completeBtn = (
            <Button
              key="complete"
              type="primary"
              size="small"
              ghost={!isMobile}
              shape={isMobile ? 'circle' : undefined}
              icon={<CheckCircleOutlined />}
              onClick={() => onOpenComplete(record)}
            >
              {!isMobile && t('imageRequests.actions.complete')}
            </Button>
          );
          actionBtns.push(
            isMobile ? (
              <Tooltip title={t('imageRequests.actions.complete')}>
                {completeBtn}
              </Tooltip>
            ) : (
              completeBtn
            )
          );
        }
        if (canDelete) {
          const delBtn = (
            <Button
              size="small"
              danger
              shape={isMobile ? 'circle' : undefined}
              icon={<DeleteOutlined />}
            >
              {!isMobile && t('common.delete')}
            </Button>
          );
          actionBtns.push(
            <Popconfirm
              key="delete"
              title={t('imageRequests.actions.deleteConfirm')}
              onConfirm={() => onDelete(record)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              {isMobile ? (
                <Tooltip title={t('common.delete')}>{delBtn}</Tooltip>
              ) : (
                delBtn
              )}
            </Popconfirm>
          );
        }
        if (!actionBtns.length) return null;
        return (
          <Row gutter={isMobile ? 4 : 8} wrap={false}>
            {actionBtns.map((btn, idx) => (
              <Col key={idx}>{btn}</Col>
            ))}
          </Row>
        );
      },
      fixed: 'right',
      width: isMobile ? 70 : 200,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={Array.isArray(items) ? items : []}
      columns={columns}
      rowKey={(r) => r.id || r._id || r.uuid}
      pagination={{
        current: currentPage,
        pageSize: perPage,
        total: total,
        onChange: onPageChange,
        showQuickJumper: true,
      }}
      scroll={{ x: 1200 }}
      size="middle"
      style={{ overflowWrap: 'normal' }}
    />
  );
};

export default ImageRequestsTable;
